import { Node } from 'prosemirror-model';
import { EditorState, Plugin, Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';

export class ImagePlaceholderAction {
  constructor(readonly id: any) {}
}

export class ImagePlaceholderAddAction extends ImagePlaceholderAction {
  constructor(
    override readonly id: any,
    readonly pos: number,
  ) {
    super(id);
  }
}

export class ImagePlaceholderRemoveAction extends ImagePlaceholderAction {}

export interface PlaceholderRef {
  id: any;
  replace(node: Node);
  clear();
}

export class ImagePlaceholderPlugin extends Plugin {
  static META_KEY = 'image-placeholder';
  static _singleton: ImagePlaceholderPlugin;

  static instance() {
    if (!this._singleton) {
      this._singleton = new ImagePlaceholderPlugin();
    }

    return this._singleton;
  }

  static add(view: EditorView) {
    return this.instance().add(view);
  }

  constructor() {
    super({
      state: {
        init() {
          return DecorationSet.empty;
        },
        apply: (tr, set) => this.apply(tr, set),
      },
      props: {
        decorations: state => this.getState(state),
      },
    });
  }

  apply(tr: Transaction, set: DecorationSet) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore-next-line
    set = set.map(tr.mapping, tr.doc);
    const action = tr.getMeta(ImagePlaceholderPlugin.META_KEY);

    if (action) {
      if (action instanceof ImagePlaceholderAddAction) {
        const widget = document.createElement('img');
        widget.setAttribute('class', 'placeholder');
        const decoration = Decoration.widget(action.pos, widget, {
          id: action.id,
        });
        set = set.add(tr.doc, [decoration]);
      } else if (action instanceof ImagePlaceholderRemoveAction) {
        set = set.remove(set.find(null, null, spec => spec.id === action.id));
      }
    }

    return set;
  }

  find(state: EditorState, id: any) {
    const decorations = this.getState(state);
    const found = decorations.find(null, null, spec => spec.id === id);
    return found.length ? found[0].from : null;
  }

  add(view: EditorView) {
    const tr = view.state.tr;
    if (!tr.selection.empty) {
      tr.deleteSelection();
    }

    const id = {};
    tr.setMeta(
      ImagePlaceholderPlugin.META_KEY,
      new ImagePlaceholderAddAction(id, tr.selection.from),
    );
    view.dispatch(tr);

    return {
      id,
      replace: node => {
        const pos = this.find(view.state, id);

        if (pos) {
          view.dispatch(
            view.state.tr
              .replaceWith(pos, pos, node)
              .setMeta(
                ImagePlaceholderPlugin.META_KEY,
                new ImagePlaceholderRemoveAction(id),
              ),
          );
        }
      },
      clear: () => {
        view.dispatch(
          view.state.tr.setMeta(
            ImagePlaceholderPlugin.META_KEY,
            new ImagePlaceholderRemoveAction(id),
          ),
        );
      },
    } as PlaceholderRef;
  }
}

export function imagePlaceholderPlugin() {
  return ImagePlaceholderPlugin.instance();
}
